(() => {
  const initTabs = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-security-tabs]')) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.('[data-security-tabs]') || []));

    roots.forEach((root) => {
      if (root.dataset.securityTabsInitialized === 'true') return;
      root.dataset.securityTabsInitialized = 'true';

      const getButtons = () => [...root.querySelectorAll('[role="tab"]')];
      const getPanels = () => [...root.querySelectorAll('[role="tabpanel"]')];
      const media = root.querySelector('.ds-sec-tabs__media img');

      getButtons().forEach((item, index) => item.setAttribute('tabindex', index === 0 ? '0' : '-1'));

      const activateTab = (button) => {
        const target = button.dataset.tab;
        getButtons().forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
          item.setAttribute('tabindex', active ? '0' : '-1');
        });
        getPanels().forEach((panel) => {
          const active = panel.dataset.panel === target;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });
        if (media && button.dataset.image) {
          media.src = button.dataset.image;
          media.alt = button.dataset.imageAlt || '';
          media.style.objectPosition = button.dataset.imagePosition || '';
        }
      };

      root.addEventListener('click', (event) => {
        const button = event.target.closest?.('[role="tab"]');
        if (button && root.contains(button)) activateTab(button);
      });

      root.addEventListener('keydown', (event) => {
        const button = event.target.closest?.('[role="tab"]');
        if (!button || !root.contains(button)) return;
        if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const buttons = getButtons();
        const index = buttons.indexOf(button);
        let next = index;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = buttons.length - 1;
        else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % buttons.length;
        else next = (index - 1 + buttons.length) % buttons.length;
        buttons[next].focus();
        activateTab(buttons[next]);
      });
    });
  };

  const initRisksCarousel = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-security-risks-carousel]')) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.('[data-security-risks-carousel]') || []));

    roots.forEach((root) => {
      if (root.dataset.securityRisksInitialized === 'true') return;

      const track = root.querySelector('[data-security-risks-track]');
      const pagination = root.querySelector('[data-security-risks-pagination]');
      const prevButton = root.querySelector('[data-security-risks-prev]');
      const nextButton = root.querySelector('[data-security-risks-next]');
      if (!track || !pagination) return;

      root.dataset.securityRisksInitialized = 'true';
      const slides = [...track.children];
      let snapPositions = [];

      const getSnapPositions = () => {
        const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
        if (maxScroll <= 4) return [0];

        const firstOffset = slides[0]?.offsetLeft || 0;
        const positions = slides.map((slide) =>
          Math.min(Math.max(slide.offsetLeft - firstOffset, 0), maxScroll)
        );
        positions.push(maxScroll);

        const unique = positions.filter((position, index, all) =>
          index === 0 || Math.abs(position - all[index - 1]) > 2
        );

        if (unique.length <= 3) return unique;

        return [
          unique[0],
          unique[Math.floor((unique.length - 1) / 2)],
          unique[unique.length - 1],
        ];
      };

      const getNearestIndex = () => {
        const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
        const firstOffset = slides[0]?.offsetLeft || 0;
        return slides.reduce((closestIndex, slide, index) => {
          const position = Math.min(Math.max(slide.offsetLeft - firstOffset, 0), maxScroll);
          const closestPosition = Math.min(
            Math.max(slides[closestIndex].offsetLeft - firstOffset, 0),
            maxScroll
          );
          return Math.abs(position - track.scrollLeft) < Math.abs(closestPosition - track.scrollLeft)
            ? index
            : closestIndex;
        }, 0);
      };

      const buildPagination = (positions) => {
        snapPositions = positions;
        pagination.innerHTML = '';
        snapPositions.forEach((position, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.setAttribute('aria-label', `Page ${index + 1} sur ${snapPositions.length}`);
          dot.addEventListener('click', () => {
            track.scrollTo({ left: position, behavior: 'smooth' });
          });
          pagination.appendChild(dot);
        });
      };

      const updateControls = () => {
        const nextPositions = getSnapPositions();
        const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
        const hasOverflow = maxScroll > 4;

        if (
          nextPositions.length !== snapPositions.length ||
          nextPositions.some((position, index) => Math.abs(position - (snapPositions[index] || 0)) > 2)
        ) {
          buildPagination(nextPositions);
        }

        const activeIndex = snapPositions.reduce((closestIndex, position, index) => (
          Math.abs(position - track.scrollLeft) < Math.abs(snapPositions[closestIndex] - track.scrollLeft)
            ? index
            : closestIndex
        ), 0);

        [...pagination.children].forEach((dot, index) => {
          const active = index === activeIndex;
          dot.classList.toggle('is-active', active);
          if (active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

        pagination.hidden = !hasOverflow;

        if (prevButton) {
          prevButton.disabled = !hasOverflow || track.scrollLeft <= 2;
          prevButton.hidden = !hasOverflow;
        }
        if (nextButton) {
          nextButton.disabled = !hasOverflow || track.scrollLeft >= maxScroll - 2;
          nextButton.hidden = !hasOverflow;
        }
      };

      const scrollByDirection = (direction) => {
        const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
        const nextIndex = Math.min(Math.max(getNearestIndex() + direction, 0), slides.length - 1);
        const firstOffset = slides[0]?.offsetLeft || 0;
        const target = Math.min(Math.max((slides[nextIndex]?.offsetLeft || 0) - firstOffset, 0), maxScroll);
        track.scrollTo({ left: target, behavior: 'smooth' });
      };

      prevButton?.addEventListener('click', () => scrollByDirection(-1));
      nextButton?.addEventListener('click', () => scrollByDirection(1));
      track.addEventListener('scroll', updateControls, { passive: true });
      window.addEventListener('resize', updateControls, { passive: true });
      if ('ResizeObserver' in window) {
        new ResizeObserver(updateControls).observe(track);
      }
      updateControls();
    });
  };

  const syncResponsiveFieldContent = () => {
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    document.querySelectorAll('[data-placeholder-mobile]').forEach((field) => {
      field.setAttribute('placeholder', mobile ? field.dataset.placeholderMobile : field.dataset.placeholderDesktop);
    });
  };

  const boot = (scope) => {
    initTabs(scope);
    initRisksCarousel(scope);
    syncResponsiveFieldContent();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  if (!window.__domoxaSecurityResponsiveFieldContentBound) {
    window.addEventListener('resize', syncResponsiveFieldContent, { passive: true });
    window.__domoxaSecurityResponsiveFieldContentBound = true;
  }

  if (!window.__domoxaSecurityTabsLoadBound) {
    document.addEventListener('shopify:section:load', (event) => { boot(event.target); });
    window.__domoxaSecurityTabsLoadBound = true;
  }
})();
